import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Autopièce Malin";
const DEFAULT_DESCRIPTION =
  "Comparateur intelligent de pièces détachées auto, moto et poids lourd. Entre ta plaque d'immatriculation, on identifie ton véhicule et on compare instantanément les prix chez Oscaro, Autodoc, Mister Auto, Ovoko…";
const DEFAULT_IMAGE = "/og-image.png";

/**
 * SEO : balises title/meta/og/twitter + canonical + JSON-LD facultatif.
 */
export const SEO = ({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd,
  noindex = false,
  publishedTime,
  modifiedTime,
  author,
}) => {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} · Comparateur pièces auto, moto & poids lourd`;
  const desc = description || DEFAULT_DESCRIPTION;
  const url = canonical || (typeof window !== "undefined" ? window.location.href.split("?")[0] : "");

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large" />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="fr_FR" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * JSON-LD global pour la racine (WebSite + SearchAction + Organization).
 */
export const GlobalSchemaOrg = () => {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://autopiece-malin.fr";
  const data = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: origin,
      logo: `${origin}/og-image.png`,
      sameAs: [],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        availableLanguage: ["French"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: origin,
      inLanguage: "fr-FR",
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/?plaque={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ];
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};
